import { calculateDailyBaselines, indexByDate } from '@vitalai/baseline-engine';
import type {
  DailyBaselines,
  DailyScores,
  MetricBaseline,
  NormalizedDailyMetrics,
  NutritionMetrics,
} from '@vitalai/health-models';
import { addDays, clamp, lastNDateKeys, round } from '@vitalai/shared';
import {
  ACTIVITY_SCORE_WEIGHTS,
  GENERAL_TARGETS,
  NUTRITION_SCORE_WEIGHTS,
  READINESS_WEIGHTS,
  RECOVERY_SCORE_WEIGHTS,
  SLEEP_SCORE_WEIGHTS,
} from './scoringConfig';

type ScoreDirection = 'higher_better' | 'lower_better';

function scoreFromBaseline(
  baseline: MetricBaseline | null | undefined,
  direction: ScoreDirection,
  sensitivity = 2.2,
): number | null {
  if (!baseline) return null;
  const signedDiff = direction === 'higher_better' ? baseline.percentDifference : -baseline.percentDifference;
  return clamp(50 + signedDiff * sensitivity, 0, 100);
}

function scoreFromTarget(
  current: number,
  target: number,
  direction: ScoreDirection = 'higher_better',
  sensitivity = 1.5,
): number {
  if (target === 0) return 50;
  const percentDiff = ((current - target) / target) * 100;
  const signedDiff = direction === 'higher_better' ? percentDiff : -percentDiff;
  return clamp(50 + signedDiff * sensitivity, 0, 100);
}

function weightedAverage(components: Array<{ value: number; weight: number }>): number {
  const totalWeight = components.reduce((sum, c) => sum + c.weight, 0);
  if (totalWeight === 0) return 50;
  const sum = components.reduce((acc, c) => acc + c.value * c.weight, 0);
  return round(sum / totalWeight, 0);
}

function isWithinRange(value: number, [min, max]: [number, number]): boolean {
  return value >= min && value <= max;
}

export function calculateSleepScore(day: NormalizedDailyMetrics, baselines: DailyBaselines): number {
  const durationScore =
    scoreFromBaseline(baselines.metrics.sleepDuration?.[30], 'higher_better') ??
    scoreFromTarget(day.sleep.totalMinutes, GENERAL_TARGETS.sleepDurationMinutes);

  const efficiencyScore = scoreFromTarget(day.sleep.efficiency, GENERAL_TARGETS.sleepEfficiencyPercent);
  const deepScore = scoreFromBaseline(baselines.metrics.deepSleep?.[30], 'higher_better') ?? 60;
  const remScore = scoreFromBaseline(baselines.metrics.remSleep?.[30], 'higher_better') ?? 60;
  const latencyScore = scoreFromTarget(day.sleep.latencyMinutes, GENERAL_TARGETS.sleepLatencyMinutes, 'lower_better');
  const awakeningsScore = scoreFromTarget(Math.max(day.sleep.awakenings, 0.5), 1, 'lower_better');

  return weightedAverage([
    { value: durationScore, weight: SLEEP_SCORE_WEIGHTS.duration },
    { value: efficiencyScore, weight: SLEEP_SCORE_WEIGHTS.efficiency },
    { value: deepScore, weight: SLEEP_SCORE_WEIGHTS.deepSleep },
    { value: remScore, weight: SLEEP_SCORE_WEIGHTS.remSleep },
    { value: latencyScore, weight: SLEEP_SCORE_WEIGHTS.latency },
    { value: awakeningsScore, weight: SLEEP_SCORE_WEIGHTS.awakenings },
  ]);
}

export function calculateRecoveryScore(
  day: NormalizedDailyMetrics,
  baselines: DailyBaselines,
  sleepScore: number,
  previousDayExerciseMinutes = 0,
): number {
  const hrvScore = scoreFromBaseline(baselines.metrics.hrv?.[30], 'higher_better') ?? 50;
  const rhrScore = scoreFromBaseline(baselines.metrics.restingHeartRate?.[30], 'lower_better') ?? 50;
  const respiratoryScore = isWithinRange(day.recovery.respiratoryRate, GENERAL_TARGETS.respiratoryRateRange)
    ? 80
    : 50;
  const spo2Score =
    day.recovery.spo2 >= GENERAL_TARGETS.spo2Min ? 85 : scoreFromTarget(day.recovery.spo2, GENERAL_TARGETS.spo2Min);
  const temperatureScore = scoreFromTarget(Math.abs(day.recovery.temperatureDeviation), 0.3, 'lower_better');

  const baseScore = weightedAverage([
    { value: hrvScore, weight: RECOVERY_SCORE_WEIGHTS.hrv },
    { value: rhrScore, weight: RECOVERY_SCORE_WEIGHTS.restingHeartRate },
    { value: respiratoryScore, weight: RECOVERY_SCORE_WEIGHTS.respiratoryRate },
    { value: spo2Score, weight: RECOVERY_SCORE_WEIGHTS.spo2 },
    { value: temperatureScore, weight: RECOVERY_SCORE_WEIGHTS.temperatureStability },
  ]);

  const sleepAdjustment = (sleepScore - 50) * 0.1;
  const trainingLoadPenalty = previousDayExerciseMinutes > 90 ? 10 : 0;
  return clamp(round(baseScore + sleepAdjustment - trainingLoadPenalty), 0, 100);
}

function weeklyLoadBalanceScore(weeklyMinutes: number): number {
  const [min, max] = GENERAL_TARGETS.weeklyExerciseMinutesRange;
  if (weeklyMinutes < min / 2.5) return 40;
  if (weeklyMinutes <= max) return 85;
  if (weeklyMinutes <= max * 1.4) return 65;
  return 45;
}

export function calculateActivityScore(
  day: NormalizedDailyMetrics,
  last7Days: NormalizedDailyMetrics[],
  recoveryScore: number,
): number {
  const stepsScore = scoreFromTarget(day.activity.steps, GENERAL_TARGETS.stepsGoal);
  const exerciseScore = scoreFromTarget(day.activity.exerciseMinutes, GENERAL_TARGETS.exerciseMinutesGoal);
  const weeklyMinutes = last7Days.reduce((sum, d) => sum + d.activity.exerciseMinutes, 0);
  const weeklyLoadScore = weeklyLoadBalanceScore(weeklyMinutes);

  // No premiar automaticamente exceso de entrenamiento cuando la recuperacion esta baja.
  const isOvertrainingRisk = recoveryScore < 50 && day.activity.exerciseMinutes > GENERAL_TARGETS.exerciseMinutesGoal * 1.5;
  const recoveryAlignmentScore = isOvertrainingRisk ? 40 : 80;

  return weightedAverage([
    { value: stepsScore, weight: ACTIVITY_SCORE_WEIGHTS.steps },
    { value: exerciseScore, weight: ACTIVITY_SCORE_WEIGHTS.exerciseMinutes },
    { value: weeklyLoadScore, weight: ACTIVITY_SCORE_WEIGHTS.weeklyLoadBalance },
    { value: recoveryAlignmentScore, weight: ACTIVITY_SCORE_WEIGHTS.recoveryAlignment },
  ]);
}

function macroBalanceHeuristic(nutrition: NutritionMetrics): number {
  const totalGrams = nutrition.protein + nutrition.carbs + nutrition.fat;
  if (totalGrams === 0) return 50;
  const proteinRatio = nutrition.protein / totalGrams;
  return clamp(50 + (proteinRatio - 0.2) * 250, 0, 100);
}

export function calculateNutritionScore(day: NormalizedDailyMetrics, baselines: DailyBaselines): number {
  const calorieBaseline = baselines.metrics.nutritionCalories?.[14];
  const calorieScore = calorieBaseline
    ? clamp(100 - Math.abs(calorieBaseline.percentDifference) * 1.5, 0, 100)
    : 60;

  const proteinTarget = day.body.weightKg
    ? day.body.weightKg * GENERAL_TARGETS.proteinPerKg
    : GENERAL_TARGETS.proteinTargetGramsFallback;
  const proteinScore = scoreFromTarget(day.nutrition.protein, proteinTarget);
  const macroBalanceScore = macroBalanceHeuristic(day.nutrition);
  const fiberScore = scoreFromTarget(day.nutrition.fiber, GENERAL_TARGETS.fiberTargetGrams);
  const sugarSodiumScore = weightedAverage([
    { value: scoreFromTarget(day.nutrition.sugar, GENERAL_TARGETS.sugarLimitGrams, 'lower_better'), weight: 1 },
    { value: scoreFromTarget(day.nutrition.sodium, GENERAL_TARGETS.sodiumLimitMg, 'lower_better'), weight: 1 },
  ]);

  return weightedAverage([
    { value: calorieScore, weight: NUTRITION_SCORE_WEIGHTS.calorieAdherence },
    { value: proteinScore, weight: NUTRITION_SCORE_WEIGHTS.protein },
    { value: macroBalanceScore, weight: NUTRITION_SCORE_WEIGHTS.macroBalance },
    { value: fiberScore, weight: NUTRITION_SCORE_WEIGHTS.fiber },
    { value: sugarSodiumScore, weight: NUTRITION_SCORE_WEIGHTS.sugarSodiumControl },
  ]);
}

export function calculateReadinessScore(scores: {
  sleepScore: number;
  recoveryScore: number;
  activityScore: number;
  nutritionScore: number;
}): number {
  // El balance de actividad castiga tanto el sedentarismo como el exceso; el ideal esta cerca de 70.
  const activityBalanceScore = clamp(100 - Math.abs(scores.activityScore - 70), 0, 100);
  return round(
    scores.recoveryScore * READINESS_WEIGHTS.recovery +
      scores.sleepScore * READINESS_WEIGHTS.sleep +
      activityBalanceScore * READINESS_WEIGHTS.activityBalance +
      scores.nutritionScore * READINESS_WEIGHTS.nutrition,
  );
}

export function calculateDailyScores(history: NormalizedDailyMetrics[], targetDateKey: string): DailyScores {
  const byDate = indexByDate(history);
  const today = byDate.get(targetDateKey);
  if (!today) {
    throw new Error(`No hay datos normalizados para el dia ${targetDateKey}`);
  }

  const baselines = calculateDailyBaselines(history, targetDateKey);
  const sleepScore = calculateSleepScore(today, baselines);
  const yesterday = byDate.get(addDays(targetDateKey, -1));
  const recoveryScore = calculateRecoveryScore(today, baselines, sleepScore, yesterday?.activity.exerciseMinutes ?? 0);

  const last7Days = lastNDateKeys(addDays(targetDateKey, -1), 7)
    .map((key) => byDate.get(key))
    .filter((value): value is NormalizedDailyMetrics => value !== undefined);
  const activityScore = calculateActivityScore(today, last7Days, recoveryScore);
  const nutritionScore = calculateNutritionScore(today, baselines);
  const readinessScore = calculateReadinessScore({ sleepScore, recoveryScore, activityScore, nutritionScore });

  return {
    date: targetDateKey,
    sleepScore,
    recoveryScore,
    activityScore,
    nutritionScore,
    readinessScore,
  };
}
